import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth.service';
import { getErrorMessage } from '../api/utils/error-handler';

/**
 * Unified hook for handling locale changes across all headers.
 * Ensures consistent order of operations:
 * 1. Save to localStorage FIRST
 * 2. Update user locale on server (if logged in)
 * 3. Change i18n language (updates UI)
 * 4. Dispatch custom event for components that need to reload data
 */
export function useLocaleHandler() {
  const { i18n, t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [changingLocale, setChangingLocale] = useState(false);

  const currentLocale = i18n.language;

  const handleLocaleChange = useCallback(async (newLocale: string) => {
    if (changingLocale) return;

    try {
      setChangingLocale(true);

      // 1. Save to localStorage FIRST
      localStorage.setItem('locale', newLocale);

      // 2. Update user locale on server (if logged in)
      if (user) {
        await authApi.changeLocale({ locale: newLocale as 'ru' | 'en' | 'uk' });
        if (refreshUser) {
          await refreshUser();
        }
      }

      // 3. Change i18n language (updates UI)
      await i18n.changeLanguage(newLocale);

      // 4. Dispatch custom event AFTER everything is done - wiki and other components listen to this
      window.dispatchEvent(new CustomEvent('localeChanged'));
    } catch (error: unknown) {
      console.error('Failed to change locale:', error);
      // Revert i18n language on error
      const previousLocale = user?.locale || localStorage.getItem('locale') || 'ru';
      i18n.changeLanguage(previousLocale);
      alert(getErrorMessage(error, t('common:messages.error')));
    } finally {
      setChangingLocale(false);
    }
  }, [changingLocale, user, refreshUser, i18n, t]);

  return {
    currentLocale,
    changingLocale,
    handleLocaleChange,
  };
}
